import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Building, Loader2, Users, CheckCircle, Crown, Sparkles, Mail } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const API = process.env.REACT_APP_BACKEND_URL;

const SUBSCRIPTION_PLANS = {
  base: {
    name: "Base",
    max_employees: 5,
    activation_price: 29.90,
    monthly_price: 2.90
  },
  standard: {
    name: "Standard",
    max_employees: 15,
    activation_price: 39.00,
    monthly_price: 4.90
  },
  premium: {
    name: "Premium",
    max_employees: 30,
    activation_price: 49.00,
    monthly_price: 5.90
  },
  enterprise: {
    name: "Enterprise",
    max_employees: 999999,
    activation_price: 59.00,
    monthly_price: 9.90
  }
};

export default function AbbonamentoPage() {
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [requestingBonifico, setRequestingBonifico] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [showPayPal, setShowPayPal] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStatus(response.data);
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchPayPalConfig = async () => {
      try {
        const response = await axios.get(`${API}/api/paypal/client-id`);
        setPaypalClientId(response.data.client_id);
      } catch (error) {
        console.log("PayPal not configured");
      }
    };
    
    fetchStatus();
    fetchPayPalConfig();
  }, [token]);

  // Check for payment success/cancel in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sessionId = params.get("session_id");
    
    if (payment === "success" && sessionId) {
      const verifyPayment = async () => {
        try {
          const response = await axios.get(`${API}/api/stripe/checkout-status/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.payment_status === "paid") {
            toast.success("Attivazione completata! Il canone mensile partirà tra 30 giorni.");
            // Refresh status
            const statusRes = await axios.get(`${API}/api/subscription/status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setStatus(statusRes.data);
          }
        } catch (error) {
          console.error("Error verifying payment:", error);
        }
        // Clean URL
        window.history.replaceState({}, "", window.location.pathname);
      };
      verifyPayment();
    } else if (payment === "cancelled") {
      toast.error("Pagamento annullato.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [token]);

  const handleStripeUpgrade = async (planKey) => {
    setProcessingPayment(true);
    setSelectedPlan(planKey);
    try {
      const response = await axios.post(
        `${API}/api/stripe/create-checkout`,
        {
          plan_key: planKey,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Errore nella creazione del pagamento. Riprova più tardi.");
    } finally {
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };

  const handleRequestBonifico = async (planKey) => {
    setRequestingBonifico(true);
    setSelectedPlan(planKey);
    try {
      await axios.post(
        `${API}/api/subscription/request-bonifico`,
        { plan_key: planKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Richiesta inviata! Riceverai le istruzioni per il bonifico via email.");
    } catch (error) {
      console.error("Error requesting bonifico:", error);
      toast.error("Errore nell'invio della richiesta. Riprova più tardi.");
    } finally {
      setRequestingBonifico(false);
      setSelectedPlan(null);
    }
  };

  const createPayPalOrder = async (planKey) => {
    try {
      const response = await axios.post(
        `${API}/api/paypal/create-order`,
        { plan_key: planKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.order_id;
    } catch (error) {
      console.error("Error creating PayPal order:", error);
      throw error;
    }
  };

  const capturePayPalOrder = async (orderId) => {
    try {
      await axios.post(
        `${API}/api/paypal/capture-order/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Pagamento PayPal completato! Per il canone mensile automatico, inserisci una carta di credito.");
      window.location.reload();
    } catch (error) {
      console.error("Error capturing PayPal order:", error);
      toast.error("Errore nel completamento del pagamento.");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!status) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Errore nel caricamento dei dati dell'abbonamento.</p>
        </div>
      </Layout>
    );
  }

  const currentPlan = SUBSCRIPTION_PLANS[status.piano] || SUBSCRIPTION_PLANS.base;
  const planKeys = Object.keys(SUBSCRIPTION_PLANS);
  const currentPlanIndex = planKeys.indexOf(status.piano);
  
  // If not paid yet, show all plans including current one
  const availablePlans = status.payment_completed 
    ? planKeys.slice(currentPlanIndex + 1) 
    : planKeys;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="abbonamento-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Abbonamento</h1>
          <p className="text-slate-500 mt-1">Gestisci il tuo piano e i metodi di pagamento</p>
        </div>

      {/* Trial Status Banner */}
      {status.is_trial && !status.payment_completed && (
        <Card className={status.trial_expired ? "border-red-300 bg-red-50" : "border-blue-300 bg-blue-50"} data-testid="trial-banner">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`h-12 w-12 rounded-full flex items-center justify-center ${status.trial_expired ? 'bg-red-100' : 'bg-blue-100'}`}>
                <Sparkles className={`h-6 w-6 ${status.trial_expired ? 'text-red-600' : 'text-blue-600'}`} />
              </div>
              <div>
                {status.trial_expired ? (
                  <>
                    <p className="font-semibold text-red-800">Prova gratuita terminata</p>
                    <p className="text-sm text-red-600">Effettua il pagamento per continuare ad utilizzare tutte le funzionalità</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-blue-800">Prova gratuita attiva</p>
                    <p className="text-sm text-blue-600">{status.trial_days_remaining} giorni rimanenti - Scegli un piano per continuare dopo la prova</p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan Card */}
      <Card data-testid="current-plan-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            Piano Attuale
          </CardTitle>
          <CardDescription>Il tuo piano di abbonamento corrente</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-theme-50 to-theme-100 rounded-xl border border-theme-200">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 bg-white rounded-xl shadow-sm flex items-center justify-center">
                <CreditCard className="h-7 w-7 text-theme-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900" data-testid="current-plan-name">{currentPlan.name}</p>
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {status.current_employees} / {currentPlan.max_employees === 999999 ? 'illimitati' : currentPlan.max_employees} dipendenti
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-theme-600" data-testid="current-plan-activation-price">
                {currentPlan.activation_price.toFixed(2).replace('.', ',')} EUR
              </p>
              <p className="text-xs text-slate-500">attivazione</p>
              <p className="text-base font-semibold text-theme-500 mt-0.5" data-testid="current-plan-monthly-price">
                + {currentPlan.monthly_price.toFixed(2).replace('.', ',')} EUR/mese
              </p>
              {status.payment_completed && (
                <span className="inline-flex items-center gap-1 text-xs text-green-600 mt-1">
                  <CheckCircle className="h-3 w-3" />
                  Attivo
                </span>
              )}
            </div>
          </div>
          
          {status.over_limit && status.payment_completed && (
            <div className={`mt-4 p-3 rounded-lg ${status.is_blocked ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-amber-100 text-amber-700 border border-amber-200'}`}>
              {status.is_blocked 
                ? 'Limite dipendenti superato - Nuove registrazioni bloccate. Effettua l\'upgrade per sbloccare.'
                : `Limite dipendenti superato - Hai ancora ${status.grace_days_remaining} giorni per effettuare l'upgrade prima del blocco.`
              }
            </div>
          )}

          {/* Card needed for monthly banner (PayPal/Bonifico users) */}
          {status.needs_card_for_monthly && status.payment_completed && (
            <div className="mt-4 p-3 rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
              Per attivare il canone mensile automatico, usa il pulsante "Paga con Carta" su un piano qualsiasi. La carta verrà salvata per gli addebiti mensili futuri.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Plans */}
      {availablePlans.length > 0 && (
        <Card data-testid="available-plans">
          <CardHeader>
            <CardTitle>
              {status.payment_completed ? "Upgrade Disponibili" : "Scegli il tuo Piano"}
            </CardTitle>
            <CardDescription>
              {status.payment_completed 
                ? "Passa a un piano superiore per più funzionalità e dipendenti"
                : "Prova gratuita 14 giorni - Tutti i piani includono attivazione + canone mensile"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {availablePlans.map((planKey) => {
                const plan = SUBSCRIPTION_PLANS[planKey];
                const isCurrentPlan = planKey === status.piano;
                const isPopular = planKey === "standard";
                
                return (
                  <div 
                    key={planKey}
                    data-testid={`plan-card-${planKey}`}
                    className={`relative rounded-xl border-2 p-5 transition-all ${
                      isCurrentPlan 
                        ? 'border-theme-400 bg-theme-50 shadow-md' 
                        : isPopular 
                          ? 'border-amber-300 bg-amber-50/50 shadow-md hover:shadow-lg' 
                          : 'border-slate-200 bg-white hover:border-theme-300 hover:shadow-md'
                    }`}
                  >
                    {isPopular && !isCurrentPlan && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Consigliato
                        </span>
                      </div>
                    )}
                    
                    {isCurrentPlan && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-theme-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Piano Attuale
                        </span>
                      </div>
                    )}
                    
                    <div className="text-center mb-3 pt-2">
                      <p className="text-lg font-bold text-slate-900">{plan.name}</p>
                      <div className="mt-2">
                        <p className="text-2xl font-bold text-theme-600" data-testid={`plan-activation-price-${planKey}`}>
                          {plan.activation_price.toFixed(2).replace('.', ',')} EUR
                        </p>
                        <p className="text-xs text-slate-500">attivazione (una tantum)</p>
                        <p className="text-lg font-semibold text-theme-500 mt-1" data-testid={`plan-monthly-price-${planKey}`}>
                          + {plan.monthly_price.toFixed(2).replace('.', ',')} EUR
                          <span className="text-sm font-normal text-slate-500">/mese</span>
                        </p>
                      </div>
                      <p className="text-sm text-slate-500 mt-2">
                        Fino a {plan.max_employees === 999999 ? 'illimitati' : plan.max_employees} dipendenti
                      </p>
                    </div>

                    {/* Payment Options */}
                    {!isCurrentPlan && (
                      <>
                        {showPayPal === planKey ? (
                          <div className="space-y-2">
                            <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "EUR" }}>
                              <PayPalButtons
                                style={{ layout: "vertical", height: 40 }}
                                createOrder={() => createPayPalOrder(planKey)}
                                onApprove={(data) => capturePayPalOrder(data.orderID)}
                                onCancel={() => setShowPayPal(null)}
                                onError={(err) => {
                                  console.error("PayPal error:", err);
                                  toast.error("Errore PayPal. Riprova.");
                                  setShowPayPal(null);
                                }}
                              />
                            </PayPalScriptProvider>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowPayPal(null)}
                              className="w-full text-xs"
                            >
                              Annulla
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Button
                              data-testid={`pay-card-${planKey}`}
                              onClick={() => handleStripeUpgrade(planKey)}
                              disabled={processingPayment || requestingBonifico}
                              className="w-full btn-theme gap-2"
                            >
                              {processingPayment && selectedPlan === planKey ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <CreditCard className="h-4 w-4" />
                              )}
                              Paga con Carta
                            </Button>
                            
                            <div className="flex gap-2">
                              {paypalClientId && (
                                <Button
                                  data-testid={`pay-paypal-${planKey}`}
                                  variant="outline"
                                  onClick={() => setShowPayPal(planKey)}
                                  disabled={processingPayment || requestingBonifico}
                                  className="flex-1 gap-1"
                                >
                                  <span className="text-blue-600 font-bold text-sm">Pay</span>
                                  <span className="text-blue-400 font-bold text-sm">Pal</span>
                                </Button>
                              )}
                              
                              <Button
                                data-testid={`pay-bonifico-${planKey}`}
                                variant="outline"
                                onClick={() => handleRequestBonifico(planKey)}
                                disabled={processingPayment || requestingBonifico}
                                className="flex-1 gap-1"
                              >
                                {requestingBonifico && selectedPlan === planKey ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Building className="h-4 w-4" />
                                )}
                                Bonifico
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Piano Personalised */}
      <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-white" data-testid="plan-personalised">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5 text-purple-600" />
            Piano Personalised
          </CardTitle>
          <CardDescription>
            Soluzione su misura per aziende con esigenze specifiche
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 bg-white rounded-xl border border-purple-100">
            <div className="flex-1">
              <p className="font-semibold text-slate-900 mb-2">Tutto del piano Enterprise, più:</p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-500" />
                  Gestione Multi-Filiali
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-500" />
                  Configurazione su misura
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-500" />
                  Supporto dedicato 24/7
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-500" />
                  Dipendenti illimitati
                </li>
              </ul>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-purple-600 mb-3">Prezzo su misura</p>
              <a
                href="mailto:support@ferie-e-permessi.it?subject=Richiesta%20Preventivo%20Piano%20Personalised&body=Buongiorno%2C%0A%0Asono%20interessato%20al%20piano%20Personalised%20per%20la%20gestione%20multi-filiali.%0A%0AInformazioni%20Azienda%3A%0A-%20nome%20Azienda%3A%0A-%20numero%20filiali%3A%0A-%20numero%20totale%20dipendenti%3A%0A-%20esigenze%20specifiche%3A%0A%0AContattatemi%20per%20un%20preventivo%20personalizzato.%0A%0AGrazie"
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
                data-testid="request-quote-btn"
              >
                <Mail className="h-4 w-4" />
                Richiedi il preventivo
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <div className="p-4 bg-slate-100 rounded-lg text-sm text-slate-600" data-testid="payment-info-box">
        <p className="font-medium mb-2">Informazioni sui pagamenti</p>
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li><strong>Prova gratuita:</strong> 14 giorni senza impegno per tutti i piani</li>
          <li><strong>Attivazione:</strong> pagamento una tantum al termine della prova</li>
          <li><strong>Canone mensile:</strong> parte 30 giorni dopo l'attivazione, addebitato automaticamente sulla carta</li>
          <li><strong>Carta di credito:</strong> pagamenti sicuri tramite Stripe + addebito mensile automatico</li>
          <li><strong>PayPal:</strong> solo per il pagamento di attivazione. Per il mensile automatico serve una carta</li>
          <li><strong>Bonifico:</strong> solo per l'attivazione. Per il mensile automatico serve una carta</li>
        </ul>
      </div>
      </div>
    </Layout>
  );
}
